import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import KeyIcon from "@mui/icons-material/Key";
import LogoutIcon from "@mui/icons-material/Logout";
import type { SimplePaletteColorOptions } from "@mui/material";
import {
  Button,
  Container,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import axios, { AxiosError } from "axios";

import { deactivateAccount, getUser } from "../../api/user";
import { themeOptions } from "../../app/theme";
import { TextMaskCustom } from "../../components/DocumentTextField";
import HidenTextField from "../../components/HidenTextField";
import Loading from "../../components/Loading";
import { notify } from "../../pages/public/components/Toast";
import extractInnerMessage from "../../utils/apiErrorUtils";
import getUserLocal from "../../utils/getUser";

TextMaskCustom.displayName = "TextMaskCustom";

function MyAccountPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = getUserLocal()?.id as number | undefined;
  const { data, error } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => await getUser(userId as number),
    enabled: Boolean(userId),
    retry: false,
  });

  useEffect(() => {
    if (error) {
      navigate({ to: "/error" });
    }
  }, [error, navigate]);
  const name = data?.name || "";
  const document = data?.document || "";
  const errorColor = themeOptions.palette?.error as SimplePaletteColorOptions;
  const [openModalExit, setOpenModalExit] = useState(false);

  // Modal desativação de conta
  const [openModalDeactivate, setOpenModalDeactivate] = useState(false);
  const [deactivateStep, setDeactivateStep] = useState<1 | 2>(1);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const handleCloseDeactivateModal = () => {
    setOpenModalDeactivate(false);
    setDeactivateStep(1);
    setDeactivateReason("");
    setDeactivatePassword("");
    setDeactivateError(null);
  };

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      if (!userId) return;
      await deactivateAccount(userId, {
        password: deactivatePassword,
        description: deactivateReason,
      });
    },
    onSuccess: () => {
      notify(t("myAccount.deactivateSuccess"), "success", "");
      handleCloseDeactivateModal();
      void navigate({ to: "/logout" });
    },
    onError: (error) => {
      const apiErrorMessage = extractInnerMessage(error);
      console.error("deactivate account error:", apiErrorMessage);

      let msg = apiErrorMessage;
      let code = "";

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const data = axiosError.response?.data as {
          message?: string;
          code?: string;
        };
        if (data?.message) {
          msg = data.message;
        }
        if (data?.code) {
          code = data.code;
        }
      }

      if (!msg || msg === "undefined") {
        msg = t("myAccount.deactivateError");
      }

      setDeactivateError(msg);
      notify(msg, "error", code, true);
    },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Container
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          className="container"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "24px",
                sm: "24px",
                md: "32px",
                lg: "32px",
                xl: "32px",
              },
              fontWeight: 500,
            }}
          >
            {t("myAccount.title")}
          </Typography>
          <Box
            gap={2}
            sx={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <TextField
              color="primary"
              disabled
              fullWidth
              label={t("myAccount.fullNameLabel")}
              size="small"
              sx={{
                "& .MuiFormLabel-root": {
                  color: "text.disabled",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              value={name}
              variant="outlined"
            />
            <TextField
              InputProps={{
                inputComponent: TextMaskCustom as never,
                inputProps: {
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  mask: "000.000.000-00",
                },
              }}
              color="primary"
              disabled
              fullWidth
              label={t("myAccount.cpfLabel")}
              size="small"
              sx={{
                "& .MuiFormLabel-root": {
                  color: "text.disabled",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              value={document}
              variant="outlined"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              mt: 3,
              mb: 2,
            }}
          >
            <Button
              color="primary"
              component={Link}
              fullWidth
              size="large"
              to="/"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                py: 1,
              }}
            >
              {t("common.home")}
            </Button>
          </Box>
          <Box sx={{ width: "100%" }}>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    void navigate({ to: "/edit-password" });
                  }}
                >
                  <KeyIcon
                    color="primary"
                    fontSize="medium"
                    style={{ marginRight: "24px" }}
                  />
                  <ListItemText primary={t("myAccount.editPassword")} />
                  <ChevronRightIcon fontSize="small" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setOpenModalExit(true);
                  }}
                >
                  <ListItemText
                    primary={t("myAccount.logout")}
                    sx={{ color: errorColor.main }}
                  />
                  <LogoutIcon color="error" fontSize="small" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
          <Divider sx={{ my: 6, width: "100%" }} />
          <Box sx={{ width: "100%" }}>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setOpenModalDeactivate(true);
                  }}
                >
                  <ListItemText
                    primary={t("myAccount.deactivateAccount")}
                    sx={{ color: errorColor.main }}
                  />
                  <HighlightOffIcon color="error" fontSize="small" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
          <Dialog
            onClose={() => {
              setOpenModalExit(false);
            }}
            open={openModalExit}
          >
            <DialogContent sx={{ padding: "16px" }}>
              <Typography
                component="h2"
                fontFamily="Inter"
                id="spring-modal-title"
                variant="h6"
              >
                {t("myAccount.logoutDialogTitle")}
              </Typography>
              <Typography
                fontFamily="Inter"
                fontWeight={400}
                id="spring-modal-description"
                sx={{ mt: 2 }}
                variant="body1"
              >
                {t("myAccount.logoutDialogMessage")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: "1rem",
                  marginTop: "2rem",
                }}
              >
                <Button
                  color="primary"
                  onClick={() => {
                    void navigate({ to: "/logout" });
                  }}
                  size="medium"
                  variant="contained"
                >
                  {t("myAccount.logoutButton")}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    setOpenModalExit(false);
                  }}
                  size="medium"
                  variant="outlined"
                >
                  {t("myAccount.stayButton")}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <Dialog
            fullWidth
            maxWidth="sm"
            onClose={handleCloseDeactivateModal}
            open={openModalDeactivate}
          >
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                component="h2"
                fontFamily="Inter"
                sx={{ fontWeight: 600, mb: 2 }}
                variant="h6"
              >
                {t("myAccount.deactivateModalTitle")}
              </Typography>

              {deactivateStep === 1 ? (
                <>
                  <Typography
                    fontFamily="Inter"
                    fontWeight={400}
                    sx={{ color: "text.primary", mb: 3 }}
                    variant="body1"
                  >
                    {t("myAccount.deactivateModalWarning")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "1rem",
                      mt: 3,
                    }}
                  >
                    <Button
                      color="primary"
                      onClick={handleCloseDeactivateModal}
                      size="medium"
                      variant="outlined"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      color="error"
                      onClick={() => setDeactivateStep(2)}
                      size="medium"
                      variant="contained"
                    >
                      {t("myAccount.proceedButton")}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    fontFamily="Inter"
                    fontWeight={400}
                    sx={{ color: "text.secondary", mb: 2 }}
                    variant="body2"
                  >
                    {t("myAccount.deactivateModalSubtitle")}
                  </Typography>
                  <Stack gap={2} sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label={t("myAccount.deactivationReasonLabel")}
                      multiline
                      onChange={(e) => setDeactivateReason(e.target.value)}
                      placeholder={t("myAccount.deactivationReasonPlaceholder")}
                      rows={3}
                      size="small"
                      value={deactivateReason}
                      variant="outlined"
                    />
                    <HidenTextField
                      fullWidth
                      label={t("myAccount.deactivationPasswordLabel")}
                      onChange={(e) => setDeactivatePassword(e.target.value)}
                      size="small"
                      value={deactivatePassword}
                      variant="outlined"
                    />
                    {deactivateError && (
                      <Typography color="error" variant="caption">
                        {deactivateError}
                      </Typography>
                    )}
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "1rem",
                      mt: 3,
                    }}
                  >
                    <Button
                      color="primary"
                      disabled={deactivateMutation.isPending}
                      onClick={() => {
                        setDeactivateStep(1);
                        setDeactivateError(null);
                      }}
                      size="medium"
                      variant="outlined"
                    >
                      {t("common.previous")}
                    </Button>
                    <Button
                      color="error"
                      disabled={
                        !deactivateReason.trim() ||
                        !deactivatePassword ||
                        deactivateMutation.isPending
                      }
                      onClick={() => deactivateMutation.mutate()}
                      size="medium"
                      variant="contained"
                    >
                      {deactivateMutation.isPending
                        ? t("myAccount.deactivatingButton")
                        : t("myAccount.confirmDeactivationButton")}
                    </Button>
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>
        </Box>
      </Container>
      <Stack
        alignItems="center"
        component="footer"
        gap={0.5}
        sx={{
          flexDirection: "column-reverse",
          pb: 2,
        }}
      >
        <Typography color="textSecondary" variant="caption">
          {t("common.smartdocsPortal")}
        </Typography>
        {typeof window.versionSystem === "string" &&
        window.versionSystem.trim() !== "" ? (
          <Typography color="textSecondary" variant="caption">
            {window.versionSystem}
          </Typography>
        ) : (
          <Typography color="textSecondary" variant="caption">
            {t("common.noVersionInfo")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export const Route = createFileRoute("/__auth/my-account")({
  component: MyAccountPage,
  pendingComponent: Loading,
});
